/**
 * AURA Intro Screen
 *
 * A 25-second animated sequence that explains what AURA is
 * while the field, brief cache, and services load in the background.
 *
 * Five acts, each 4-5 seconds:
 *   1. The world is drowning in signals. No one knows which ones matter.
 *   2. AURA watches 12 intelligence domains for Soho + Artists & Robots.
 *   3. It reads web, news, events, research — clustering what's rising.
 *   4. Every week, it writes a brief. Every activation, it remembers.
 *   5. The field is live. [ENTER →]
 *
 * Typography-first. No icons. No illustrations. Just words and motion.
 * Inspired by the kinetic text traditions of title sequences.
 */

const SESSION_KEY = 'aura_intro_seen_v2';

const ACTS = [
  {
    duration: 4500,
    lines: [
      { text: 'Soho + Artists & Robots',        size: 10, weight: 700, spacing: 0.22, opacity: 0.28, delay: 0,    caps: true  },
      { text: 'Intelligence Field', size: 28, weight: 300, spacing: 0.02, opacity: 0.82, delay: 400, caps: false },
    ],
  },
  {
    duration: 4500,
    lines: [
      { text: '12 intelligence domains.',        size: 13, weight: 400, spacing: 0.01, opacity: 0.65, delay: 0,    caps: false },
      { text: 'Watched in real time.',           size: 13, weight: 400, spacing: 0.01, opacity: 0.65, delay: 600,  caps: false },
      { text: 'Cultural shifts. Brand signals.', size: 13, weight: 400, spacing: 0.01, opacity: 0.65, delay: 1200, caps: false },
      { text: "What's rising. What to act on.",  size: 13, weight: 400, spacing: 0.01, opacity: 0.65, delay: 1800, caps: false },
    ],
  },
  {
    duration: 4500,
    lines: [
      { text: 'Every signal is a data point.',                size: 11, weight: 300, spacing: 0.01, opacity: 0.45, delay: 0,   caps: false },
      { text: 'Every cluster is an argument.',               size: 11, weight: 300, spacing: 0.01, opacity: 0.45, delay: 500,  caps: false },
      { text: 'The field is the argument made visible.',      size: 11, weight: 400, spacing: 0.01, opacity: 0.72, delay: 1100, caps: false },
    ],
  },
  {
    duration: 4500,
    lines: [
      { text: 'SENSE',    size: 11, weight: 700, spacing: 0.20, opacity: 0.55, delay: 0,    caps: true  },
      { text: 'Read the intelligence brief.',                          size: 11, weight: 300, spacing: 0.01, opacity: 0.38, delay: 200,  caps: false },
      { text: 'VALIDATE', size: 11, weight: 700, spacing: 0.20, opacity: 0.55, delay: 900,  caps: true  },
      { text: 'Is this signal worth acting on?',                       size: 11, weight: 300, spacing: 0.01, opacity: 0.38, delay: 1100, caps: false },
      { text: 'FIELD',    size: 11, weight: 700, spacing: 0.20, opacity: 0.55, delay: 1800, caps: true  },
      { text: 'Open a live activation grounded in intelligence.',      size: 11, weight: 300, spacing: 0.01, opacity: 0.38, delay: 2000, caps: false },
    ],
  },
  {
    duration: 5000,
    lines: [
      { text: 'The field is live.',  size: 18, weight: 300, spacing: 0.01, opacity: 0.70, delay: 0,    caps: false },
      { text: 'Enter →',            size: 12, weight: 500, spacing: 0.06, opacity: 0.40, delay: 1200, caps: false, isButton: true },
    ],
  },
];

export class Intro {
  private el: HTMLElement | null = null;
  private resolve: (() => void) | null = null;
  private skipped = false;

  /** Returns true if intro should be shown */
  static shouldShow(): boolean {
    return !sessionStorage.getItem(SESSION_KEY);
  }

  /** Run the intro, resolves when complete or skipped */
  run(): Promise<void> {
    return new Promise((resolve) => {
      this.resolve = resolve;
      sessionStorage.setItem(SESSION_KEY, '1');

      this.el = document.createElement('div');
      this.el.style.cssText = `
        position: fixed; inset: 0; z-index: 10000;
        background: #06060d;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        cursor: default;
        user-select: none;
      `;

      // Skip hint
      const skip = document.createElement('div');
      skip.textContent = 'Skip →';
      skip.style.cssText = `
        position: absolute; bottom: 28px; right: 28px;
        font-size: 9px; font-weight: 500; letter-spacing: 0.12em;
        text-transform: uppercase; color: rgba(255,255,255,0.18);
        cursor: pointer; transition: color 0.2s ease;
      `;
      skip.onmouseenter = () => skip.style.color = 'rgba(255,255,255,0.40)';
      skip.onmouseleave = () => skip.style.color = 'rgba(255,255,255,0.18)';
      skip.onclick = () => this.finish();
      this.el.appendChild(skip);

      // Progress line at bottom
      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        position: absolute; bottom: 0; left: 0; right: 0;
        height: 1px; background: rgba(255,255,255,0.06);
      `;
      const progressFill = document.createElement('div');
      progressFill.style.cssText = `
        height: 100%; background: rgba(255,255,255,0.20);
        width: 0; transition: width 0.5s linear;
      `;
      progressBar.appendChild(progressFill);
      this.el.appendChild(progressBar);

      document.body.appendChild(this.el);

      // Content area
      const content = document.createElement('div');
      content.style.cssText = `
        max-width: 480px; width: 100%; padding: 0 32px;
        text-align: center; position: relative;
        min-height: 160px; display: flex; flex-direction: column;
        align-items: center; justify-content: center;
      `;
      this.el.appendChild(content);

      // Run acts
      this.runActs(content, progressFill);

      // Allow clicking anywhere to skip (except last act)
      this.el.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).style.cursor === 'pointer') return;
        this.finish();
      });
    });
  }

  private async runActs(content: HTMLElement, progressFill: HTMLElement) {
    const totalDuration = ACTS.reduce((sum, a) => sum + a.duration, 0);
    let elapsed = 0;

    for (let i = 0; i < ACTS.length; i++) {
      if (this.skipped) break;
      const act = ACTS[i];

      // Fade out previous content
      if (i > 0) {
        content.style.transition = 'opacity 0.4s ease';
        content.style.opacity = '0';
        await this.sleep(450);
      }

      if (this.skipped) break;

      // Clear and rebuild content
      content.innerHTML = '';
      content.style.opacity = '0';

      for (const line of act.lines) {
        const el = document.createElement('div');
        el.style.cssText = `
          font-size: ${line.size}px;
          font-weight: ${line.weight};
          letter-spacing: ${line.spacing}em;
          ${line.caps ? 'text-transform: uppercase;' : ''}
          color: rgba(255,255,255,${line.opacity});
          line-height: ${line.size > 20 ? 1.3 : 1.8};
          margin-bottom: ${line.size > 20 ? '8px' : '4px'};
          opacity: 0;
          transition: opacity 0.5s ease;
          ${line.isButton ? 'cursor: pointer; margin-top: 20px;' : ''}
        `;
        el.textContent = line.text;
        if (line.isButton) {
          el.onclick = (e) => { e.stopPropagation(); this.finish(); };
        }
        content.appendChild(el);

        // Stagger reveal
        setTimeout(() => {
          if (!this.skipped) el.style.opacity = '1';
        }, line.delay);
      }

      // Fade in act
      content.style.transition = 'opacity 0.5s ease';
      content.style.opacity = '1';

      // Progress
      elapsed += act.duration;
      progressFill.style.width = `${(elapsed / totalDuration) * 100}%`;

      await this.sleep(act.duration);
    }

    if (!this.skipped) this.finish();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }

  finish() {
    if (this.skipped) return;
    this.skipped = true;

    if (this.el) {
      this.el.style.transition = 'opacity 0.8s ease';
      this.el.style.opacity = '0';
      setTimeout(() => {
        this.el?.remove();
        this.el = null;
      }, 850);
    }

    this.resolve?.();
  }
}
